import type { FunctionalComponent as FC } from 'vue';
import FileIcon from './fileIcon.vue';
import { CardMenu } from './cardMenu';
import EditLabel from './editLabel.vue';
import { TimeText } from './time';
import { Eye } from './eye';
import { Play, usePlayView } from './play';
import TemplateUse from './templateUse.vue';
import Collect from '~/features/collect/index.vue';
import MusicImg from '~/assets/svg/music.svg?url';
import DeleteLink from '~/assets/svg/link.svg?url';
// import PlaceholderImg from '~/assets/png/placeholder.png'
import MusicPlayer from '~/components/musicPlayer/index.vue';
import type {
  FILE_TYPE_MAP,
  COLLECTION_MAP,
  COLLECTION_SOURCE,
  RESOURCE_TYPE,
  UPLOAD_TYPE,
} from '~/utils/constant.ts';
import Resource from '~/components/resource';
import { TemplateShare } from '@/features/template/components/templateShare';
export interface ICard {
  id: number;
  name?: string;
  is_user_upload: boolean;
  cover_url?: string;
  file_url?: string;
  resource_type?: RESOURCE_TYPE;
  preview_video_list?: string[];
  size?: string;
  collect?: boolean;
  time?: string;
  edit?: boolean;
  tags?: string[];
  media_type: FILE_TYPE_MAP;
  source_is_deleted: boolean;
  title?: string;
}

export interface DataType {
  model_type?: typeof COLLECTION_MAP;
  resource_type?: RESOURCE_TYPE;
  media_type?: FILE_TYPE_MAP;
  source?: COLLECTION_SOURCE;
  upload_type?: UPLOAD_TYPE;
  [key: string]: any;
}

type IProps = {
  data?: ICard;
  edit?: boolean;
  check?: boolean;
  collect?: boolean;
  menus?: any[];
  menuFilter?: (key: any, item: any, dataType?: DataType) => boolean;
  keyItem?: any;
  source?: any;
  onUpdateData?: (key: string, value: any, data: any) => void;
  title?: string;
};

type IEvents = {
  updateData: (key: string, value: any, data: any, source?: any) => void;
};

const SourceDelete = () => (
  <div class="absolute-center flex-center text-14 p-[6px_11px] rounded-[8px] text-error-6 font-semibold whitespace-nowrap bg-[rgba(255,240,240,0.8)] backdrop-blur-[3px]">
    <Resource src={DeleteLink} class="mr-[6px] w-[14px] h-[14px]" />
    源文件已删除
  </div>
);

const ItemEye = (data: any) => <Eye data={data} />;

const ItemTemplate = (data: any, source?: any) => <TemplateUse data={data} source={source} />;
const ItemMusicPlayer = (data: any) => <MusicPlayer id={data.id} url={data?.file_url} />;

const tools: any = {
  // 场景
  [RESOURCE_TYPE.STUDIO]: [ItemEye],
  // 人物
  [RESOURCE_TYPE.PERSON]: [ItemEye],
  // 片头
  // [RESOURCE_TYPE.HEAD]: [ItemPlay],
  // 片尾
  // [RESOURCE_TYPE.END]: [ItemPlay],
  // 音乐
  [RESOURCE_TYPE.MUSIC]: [ItemMusicPlayer],
  // 贴纸
  // [RESOURCE_TYPE.PASTER]: [ItemPlay],
  // 素材
  // [RESOURCE_TYPE.MATERIAL]: [ItemPlay],
  // 模板
  [RESOURCE_TYPE.TEMPLATE]: [ItemTemplate],
};

export const Card: FC<IProps, IEvents> = (props, { emit, attrs }) => {
  const {
    data,
    edit = false,
    check = false,
    collect = false,
    menus,
    menuFilter,
    keyItem,
    source,
    title,
  } = props;

  const { $t } = useNuxtApp();

  const Imenus =
    menus?.filter(item => {
      if (!item) {
        return false;
      }

      if (item.key === 'brand_share') {
        return false;
      }

      if (item.type === 'divider') {
        return true;
      }
      const permission = checkPermission(item.key);
      if (menuFilter) {
        return permission && menuFilter?.(item.key, props?.data);
      }
      return permission;
    }) || [];
  const hasMenu = !check && Imenus.length > 0;
  const isMusic = keyItem?.resource_type === RESOURCE_TYPE.MUSIC;
  const toolItems = !check
    ? tools[keyItem?.resource_type]?.map((item: (data: ICard | undefined, source?: any) => any) =>
        item(data, source),
      )
    : [];
  const onUpdateData = (key: string, value: any, source?: any) => {
    emit('updateData', key, value, data, source);
  };

  const handItemClick = (e: MouseEvent) => {
    if (check || attrs.onClick) {
      return;
    }
    e.stopPropagation();
    if (
      ![
        RESOURCE_TYPE.HEAD,
        RESOURCE_TYPE.END,
        RESOURCE_TYPE.PASTER,
        RESOURCE_TYPE.MATERIAL,
      ].includes(keyItem?.resource_type)
    ) {
      return;
    }
    const trackParams: any = {
      target: keyItem?.target,
      source,
      click: OPEN_ENTRANCE.CARD,
    };
    usePlayView(data, trackParams);
  };

  const isHaveShare = computed(() => menus?.some((item: any) => item.key === 'brand_share'));

  return (
    <div class="brand-card">
      <div class="brand-card-cover cursor-pointer" onClick={check ? () => {} : handItemClick}>
        {isHaveShare.value && (
          <div
            class="absolute top-[10px] left-[10px] z-20 share"
            onClick={event => event?.stopPropagation()}
          >
            <TemplateShare
              title={$t('fen-xiang-kong-ge') + title}
              trackParams={{
                target: keyItem?.target,
                source,
                dataId: data?.id,
                dataTitle: data?.title,
              }}
              isCard={true}
              params={{
                share_ids: [data?.model_id],
                model_type: data?.model_type,
                resource_type:
                  data?.resource_type !== RESOURCE_TYPE.TEMPLATE ? data?.resource_type : undefined,
                media_type: data?.media_type,
                is_user_upload: data?.is_user_upload,
              }}
            />
          </div>
        )}
        <div class="small">
          <Resource
            src={isMusic ? MusicImg : data?.cover_url || PLACEHOLDER}
            transformResource={url => transformImage(url, { height: 300 })}
          />
        </div>
        {check && <a-checkbox value={data?.id} class="tools right-[10px]" />}
        {toolItems}
        {hasMenu && (
          <CardMenu menus={Imenus} data={data} keyItem={keyItem} updateData={onUpdateData} />
        )}
        {collect && (
          <Collect
            modelValue={data?.collect}
            class={`tools left-[10px] ${data?.collect ? 'showen' : 'hover'}`}
            onChange={(v: boolean) => onUpdateData('collect', v)}
            data={data}
            params={{
              is_user_upload: data?.is_user_upload,
              model_id: data?.id as number,
              model_type: keyItem.model_type,
              resource_type:
                keyItem?.resource_type !== RESOURCE_TYPE.TEMPLATE
                  ? keyItem?.resource_type
                  : undefined,
              media_type: data?.media_type,
            }}
          />
        )}
        {data?.source_is_deleted && <SourceDelete />}
      </div>
      <div class="brand-card-content">
        <div class="flex-between">
          <FileIcon type={data?.media_type} class="mr-[6px]" isHot={data?.tags?.includes('热门')} />
          <EditLabel
            label={data?.name}
            edit={edit}
            visible={data?.edit}
            onUpdate:visible={(v: boolean) => onUpdateData('edit', v, OPEN_ENTRANCE.CARD)}
            onChange={(v: string) => onUpdateData('name', v)}
            maxlength={50}
          />
          {data?.size && <span class="text-12 text-font-40 ml-[8px]">{data?.size}</span>}
        </div>
        {data?.time && <TimeText value={data?.time} />}
      </div>
    </div>
  );
};
